import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@astryxdesign/core/Section";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Card } from "@astryxdesign/core/Card";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Switch } from "@astryxdesign/core/Switch";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Banner } from "@astryxdesign/core/Banner";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { useToast } from "@astryxdesign/core/Toast";
import { Copy } from "lucide-react";
import { api, type UpdateOIDCSettingsInput } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { RetryBanner } from "../../components/RetryBanner";

interface FormValues {
  enabled: boolean;
  displayName: string;
  issuerUrl: string;
  clientId: string;
  /** A new secret to set. Empty means "leave the stored one alone" unless
   *  clearSecret is also on — see the field's own note below. */
  clientSecret: string;
  /** Explicit, separate from clientSecret being blank, because a blank
   *  field is ALSO what "don't touch it" looks like — the secret is
   *  write-only, so there's no way to show "unchanged" and "empty" as
   *  visually different states otherwise. */
  clearSecret: boolean;
  defaultRoleId: string;
}

const emptyValues: FormValues = {
  enabled: false,
  displayName: "",
  issuerUrl: "",
  clientId: "",
  clientSecret: "",
  clearSecret: false,
  defaultRoleId: "",
};

/** Admin-only configuration for signing in via an external OpenID Connect
 *  provider (Authentik, per this org's SSO mandate) — issuer, client
 *  credentials, enable/disable, and which role a new SSO account gets on
 *  its first sign-in. Gated on system.manage; see App.tsx's route table.
 *
 *  A single settings form rather than the list-plus-dialog shape most other
 *  admin pages use (Roles, Users, Projects): there is exactly one row to
 *  edit, not a collection, so a dialog opened from a table would be an
 *  empty table with one "Edit" action — this is just the form itself. */
export function OidcSettingsPage() {
  const queryClient = useQueryClient();
  const showToast = useToast();
  const [values, setValues] = useState<FormValues>(emptyValues);
  const [error, setError] = useState<string | null>(null);
  // Same guard StreamLimitsDialog uses: once the operator has touched the
  // form, a background refetch (react-query's refetch-on-window-focus, say)
  // must not silently overwrite an in-progress edit.
  const hasEditedRef = useRef(false);

  const {
    data: settings,
    isLoading,
    error: loadError,
    refetch,
    isRefetching,
  } = useQuery({ queryKey: ["oidc-settings"], queryFn: api.settings.oidc });

  // Roles are needed here for the default-role dropdown, not fetched by any
  // parent — same reasoning as UserFormDialog fetching its own role list.
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: api.roles.list });

  useEffect(() => {
    if (hasEditedRef.current) return;
    if (settings) {
      setValues({
        enabled: settings.enabled,
        displayName: settings.display_name,
        issuerUrl: settings.issuer_url,
        clientId: settings.client_id,
        clientSecret: "",
        clearSecret: false,
        defaultRoleId: settings.default_role_id,
      });
    }
  }, [settings]);

  const set = <K extends keyof FormValues>(key: K) => (value: FormValues[K]) => {
    hasEditedRef.current = true;
    setValues((v) => ({ ...v, [key]: value }));
  };

  const save = useMutation({
    mutationFn: (input: UpdateOIDCSettingsInput) => api.settings.updateOidc(input),
    onSuccess: async (updated) => {
      showToast({ body: "Single sign-on settings saved" });
      hasEditedRef.current = false;
      queryClient.setQueryData(["oidc-settings"], updated);
      await queryClient.invalidateQueries({ queryKey: ["oidc-settings"] });
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Something went wrong."),
  });

  const submit = () => {
    if (!settings) return;
    setError(null);

    if (values.enabled && (!values.issuerUrl.trim() || !values.clientId.trim() || !values.defaultRoleId)) {
      setError("Issuer URL, client ID, and a default role are all required to enable single sign-on.");
      return;
    }

    save.mutate({
      enabled: values.enabled,
      display_name: values.displayName.trim() || "Single sign-on",
      issuer_url: values.issuerUrl.trim(),
      client_id: values.clientId.trim(),
      client_secret: values.clearSecret ? "" : values.clientSecret.trim() || undefined,
      default_role_id: values.defaultRoleId,
      version: settings.version,
    });
  };

  const copyRedirectUri = () => {
    if (!settings?.redirect_uri) return;
    void navigator.clipboard.writeText(settings.redirect_uri).then(
      () => showToast({ body: "Callback URL copied" }),
      () => showToast({ body: "Couldn't copy — copy it manually", type: "error" }),
    );
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <VStack gap={3}>
        {Array.from({ length: 5 }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} height={36} index={i} />
        ))}
      </VStack>
    );
  } else if (loadError) {
    body = (
      <RetryBanner
        title="Couldn't load single sign-on settings"
        description={loadError instanceof Error ? loadError.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else {
    body = (
      <Card width={560}>
        <VStack gap={4}>
          {error && <Banner status="error" title="Couldn't save settings" description={error} />}

          <Switch
            label="Enable single sign-on"
            description="Lets people sign in with the identity provider below, alongside the existing username/password login — this never disables local sign-in."
            value={values.enabled}
            onChange={set("enabled")}
          />

          <FormLayout>
            <TextInput
              label="Sign-in button label"
              description='Shown on the login page as "Sign in with…".'
              value={values.displayName}
              onChange={set("displayName")}
              placeholder="Single sign-on"
            />
            <TextInput
              label="Issuer URL"
              description="The identity provider's OIDC issuer, e.g. https://sso.example.com/application/o/cctv/."
              value={values.issuerUrl}
              onChange={set("issuerUrl")}
              isRequired={values.enabled}
            />
            <TextInput
              label="Client ID"
              value={values.clientId}
              onChange={set("clientId")}
              isRequired={values.enabled}
            />
            <TextInput
              label="Client secret"
              type="password"
              description={
                values.clearSecret
                  ? "The stored secret will be cleared on save — leave this on only for a public client authenticating via PKCE alone."
                  : settings?.client_secret_set
                    ? "A secret is already saved. Leave this blank to keep it, or enter a new value to replace it."
                    : "No secret is saved yet. Optional — leave blank for a public client authenticating via PKCE alone."
              }
              value={values.clientSecret}
              onChange={(v) => {
                set("clientSecret")(v);
                if (v) set("clearSecret")(false);
              }}
              isDisabled={values.clearSecret}
            />
            {settings?.client_secret_set && (
              <Switch
                label="Clear the stored secret"
                value={values.clearSecret}
                onChange={(clearSecret) => {
                  set("clearSecret")(clearSecret);
                  if (clearSecret) set("clientSecret")("");
                }}
              />
            )}
            <Selector
              label="Default role for new accounts"
              description="Assigned the first time someone signs in via SSO with no matching local account yet."
              value={values.defaultRoleId}
              onChange={set("defaultRoleId")}
              options={(roles ?? []).map((r) => ({ value: r.id, label: r.name }))}
              placeholder="Choose a role"
              isRequired={values.enabled}
            />
          </FormLayout>

          {settings?.redirect_uri && (
            <VStack gap={1}>
              <HStack gap={2} vAlign="end">
                <TextInput label="Callback URL" description="Register this as the redirect URI on the identity provider's client." value={settings.redirect_uri} isDisabled />
                <IconButton
                  label="Copy callback URL"
                  tooltip="Copy callback URL"
                  icon={<Copy />}
                  variant="ghost"
                  onClick={copyRedirectUri}
                />
              </HStack>
            </VStack>
          )}

          <HStack justify="end">
            <Button label="Save" variant="primary" onClick={submit} isLoading={save.isPending} isDisabled={!settings} />
          </HStack>
        </VStack>
      </Card>
    );
  }

  return (
    <Section padding={4}>
      <VStack gap={4}>
        <PageHeader
          title="Single sign-on"
          description="Configure OIDC sign-in against an external identity provider (e.g. Authentik)."
        />
        {body}
      </VStack>
    </Section>
  );
}
